import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { 
  ArrowLeft, 
  Plus,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  Settings,
  Share,
  Receipt,
  ChevronRight,
  Mail,
  UserPlus,
  RefreshCw,
  Trash2,
  Copy,
  RotateCcw,
  QrCode
} from 'lucide-react';
import { mockExpenses, mockCategories, getGroupExpenses } from '../mock/mockData';
import { toast } from 'sonner';
import { useAuth } from '../context/CompleteAuthContext';
import PaymentModal from './PaymentModal';
import QRCollectModal from './QRCollectModal';

const GroupDetail = ({ groupId, onBack, onAddExpense }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('expenses');
  const [group, setGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '' });
  const [isInviting, setIsInviting] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleData, setSettleData] = useState({ toUserId: '', toUserName: '', amount: '' });
  const [isSettling, setIsSettling] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ open: false, toUserId: '', toUserName: '', amountCents: 0 });
  const [qrModal, setQrModal] = useState({ open: false, fromUserId: '', fromUserName: '', amountCents: 0 });

  const handleAddExpenseClick = () => {
    onAddExpense(groupId, group);
  };

  const formatSettlementDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const refetchGroup = async () => {
    setIsLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const [groupResponse, expensesResponse, settlementsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/groups/${groupId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/groups/${groupId}/expenses`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/groups/${groupId}/settlements`, { credentials: 'include' })
      ]);
      
      if (groupResponse.ok) {
        const groupData = await groupResponse.json();
        setGroup(groupData);
      }
      
      if (expensesResponse.ok) {
        const expensesData = await expensesResponse.json();
        setExpenses(expensesData);
      }

      if (settlementsResponse.ok) {
        const settlementsData = await settlementsResponse.json();
        setSettlements(settlementsData);
      }
    } catch (error) {
      console.error('Error refetching group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch group data and expenses
  useEffect(() => {
    const fetchGroupAndExpenses = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        // Fetch group
        const groupResponse = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
          credentials: 'include'
        });
        
        if (groupResponse.ok) {
          const groupData = await groupResponse.json();
          setGroup(groupData);
        } else {
          console.error('Failed to fetch group');
          toast.error('Failed to load group details');
        }

        // Fetch expenses
        const expensesResponse = await fetch(`${BACKEND_URL}/api/groups/${groupId}/expenses`, {
          credentials: 'include'
        });
        
        if (expensesResponse.ok) {
          const expensesData = await expensesResponse.json();
          setExpenses(expensesData);
        }

        // Fetch settlements
        const settlementsResponse = await fetch(`${BACKEND_URL}/api/groups/${groupId}/settlements`, {
          credentials: 'include'
        });
        
        if (settlementsResponse.ok) {
          const settlementsData = await settlementsResponse.json();
          setSettlements(settlementsData);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load group details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchGroupAndExpenses();
  }, [groupId]);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    
    if (!inviteData.name.trim() || !inviteData.email.trim()) {
      toast.error('Please enter both name and email');
      return;
    }

    setIsInviting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(inviteData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          toast.success('Invite sent.', {
            description: `${inviteData.name} will receive an email invitation.`,
          });
        } else {
          toast.warning('Invite created, but email could not be sent.', {
            description: 'Use “Copy link” next to the member to share the invite manually.',
          });
        }
        
        // Refresh group data
        const groupResponse = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
          credentials: 'include'
        });
        if (groupResponse.ok) {
          const groupData = await groupResponse.json();
          setGroup(groupData);
        }
        
        // Reset form
        setInviteData({ name: '', email: '' });
        setShowInviteForm(false);
      } else {
        const error = await response.json();
        toast.error('Failed to send invitation', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteGroup = async () => {
    setIsDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Group deleted successfully');
        // Redirect back to dashboard
        setTimeout(() => {
          onBack();
        }, 500);
      } else {
        const error = await response.json();
        toast.error('Couldn\'t delete group. Please try again.', {
          description: error.detail || 'An error occurred'
        });
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Couldn\'t delete group. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCopyInviteLink = async (member) => {
    try {
      // If member already has invite_url from backend, use it directly
      if (member.invite_url) {
        await navigator.clipboard.writeText(member.invite_url);
        toast.success('Invite link copied to clipboard!');
        return;
      }
      
      // Fallback: fetch from backend if not present
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${BACKEND_URL}/api/groups/${groupId}/invite-link?member_email=${encodeURIComponent(member.email)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        await navigator.clipboard.writeText(data.invite_url);
        toast.success('Invite link copied to clipboard!');
      } else {
        toast.error('Failed to get invite link');
      }
    } catch (error) {
      console.error('Error copying invite link:', error);
      toast.error('Failed to copy invite link');
    }
  };

  const handleResendInvite = async (memberEmail, memberName) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${BACKEND_URL}/api/groups/${groupId}/resend-invite?member_email=${encodeURIComponent(memberEmail)}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          toast.success('Invite sent.', {
            description: `Email sent to ${memberName}.`,
          });
        } else {
          toast.warning('Invite created, but email could not be sent.', {
            description: 'Use “Copy link” to share the invite manually.',
          });
        }
      } else {
        toast.error('Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invitation');
    }
  };

  const openSettleModal = (memberId, memberName, owedAmount) => {
    setSettleData({
      toUserId: memberId,
      toUserName: memberName,
      amount: Math.abs(owedAmount).toFixed(2)
    });
    setShowSettleModal(true);
  };

  const handleSettleUp = async () => {
    if (!settleData.toUserId || !settleData.amount || parseFloat(settleData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSettling(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/settlements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          from_user_id: user.id,
          to_user_id: settleData.toUserId,
          amount: parseFloat(settleData.amount)
        })
      });

      if (response.ok) {
        toast.success(`Marked as paid: ${formatCurrency(parseFloat(settleData.amount))}`);
        setShowSettleModal(false);
        setSettleData({ toUserId: '', toUserName: '', amount: '' });
        await refetchGroup();
      } else {
        const error = await response.json();
        toast.error('Failed to record settlement', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error creating settlement:', error);
      toast.error('Failed to record settlement');
    } finally {
      setIsSettling(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteExpenseId) return;
    
    setIsDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${BACKEND_URL}/api/groups/${groupId}/expenses/${deleteExpenseId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Expense deleted successfully');
        await refetchGroup();
        setShowDeleteDialog(false);
        setDeleteExpenseId(null);
      } else {
        const error = await response.json();
        toast.error('Failed to delete expense', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading group...</p>
        </Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Group not found</h2>
          <Button onClick={onBack}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-green-600';
    if (balance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getBalanceIcon = (balance) => {
    if (balance > 0) return <TrendingUp className="w-4 h-4" />;
    if (balance < 0) return <TrendingDown className="w-4 h-4" />;
    return <DollarSign className="w-4 h-4" />;
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getCategoryInfo = (categoryId) => {
    return mockCategories.find(c => c.id === categoryId) || mockCategories.find(c => c.id === 'other');
  };

  // Calculate member balances (simplified)
  // Get current user's balance - ONLY from backend
  const currentUserMember = group?.members?.find(
    m => m.user_id === user?.id || m.email === user?.email
  );
  
  const userBalance = currentUserMember?.balance ?? 0;
  
  // Calculate paid/owes for display purposes ONLY (not for balance calculation)
  const calculatePaidAndOwes = (memberId) => {
    if (!expenses || !Array.isArray(expenses)) return { paid: 0, owes: 0 };
    
    let paid = 0;
    let owes = 0;
    
    expenses.forEach(expense => {
      // Add to paid if this member was the payer
      if (expense.paid_by_user_id === memberId) {
        paid += expense.amount_total || 0;
      }
      
      // Add to owes if this member is in splitBetween
      const split = expense.splitBetween?.find(s => s.memberId === memberId);
      if (split) {
        owes += split.amount || 0;
      }
    });
    
    return { paid, owes };
  };
  
  // Debug logging
  console.log('🔍 GroupDetail Balance Debug:', {
    groupId,
    userId: user?.id,
    userEmail: user?.email,
    currentUserMember,
    backendBalance: currentUserMember?.balance,
    userBalance,
    allMembers: group?.members?.map(m => ({
      name: m.name,
      user_id: m.user_id,
      balance: m.balance
    })),
    expensesCount: expenses?.length
  });

  // Member balances with paid/owes for display
  const memberBalances = (group?.members || []).map(member => {
    const { paid, owes } = calculatePaidAndOwes(member.user_id);
    return {
      ...member,
      balance: member.balance ?? 0, // Backend balance
      paid,  // For display only
      owes   // For display only
    };
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" onClick={onBack} size="icon" className="shrink-0 h-9 w-9 sm:hidden" data-testid="group-back-mobile">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" onClick={onBack} size="sm" className="hidden sm:inline-flex shrink-0">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Avatar className="w-9 h-9 sm:w-10 sm:h-10 shrink-0">
              <AvatarImage src={group.avatar} alt={group.name} />
              <AvatarFallback>{group.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">{group.name}</h1>
              {group.description && (
                <p className="hidden sm:block text-sm text-gray-500 truncate">{group.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleAddExpenseClick}
              className="bg-blue-600 hover:bg-blue-700 h-9 sm:h-10 px-3 sm:px-4"
              data-testid="group-add-expense-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Expense</span>
            </Button>

            {/* Settings/Share menu — collapsed into one kebab on mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="group-menu-btn">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => { setActiveTab('members'); setShowInviteForm(true); }} data-testid="group-share-action">
                  <Share className="w-4 h-4 mr-2" />
                  Share group
                </DropdownMenuItem>
                {group?.owner_id === user?.id && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete group
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* User Balance Summary — calm tone */}
        {userBalance !== 0 && (
          <div
            className={`mb-4 sm:mb-5 px-4 py-3 rounded-xl flex items-center gap-3 ${
              userBalance > 0
                ? 'bg-green-50/70 text-green-800'
                : 'bg-amber-50/70 text-amber-800'
            }`}
            data-testid="group-balance-summary"
          >
            <div className={`w-2 h-2 rounded-full shrink-0 ${userBalance > 0 ? 'bg-green-500' : 'bg-amber-500'}`} />
            <p className="text-sm sm:text-[15px] font-medium tabular-nums">
              {userBalance > 0
                ? `You're owed ${formatCurrency(Math.abs(userBalance))} in this group`
                : `You owe ${formatCurrency(Math.abs(userBalance))} in this group`}
            </p>
          </div>
        )}

        {/* Group Stats — Net Balance dominant, others compact */}
        <div className="space-y-3 mb-5 sm:mb-6">
          <Card data-testid="stat-net-balance">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] sm:text-xs uppercase tracking-wide text-gray-500 mb-1">Net Balance</p>
                  <p className={`text-2xl sm:text-3xl font-bold tabular-nums whitespace-nowrap ${getBalanceColor(userBalance)}`}>
                    {userBalance >= 0 ? '+' : '-'}{formatCurrency(Math.abs(userBalance))}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {userBalance > 0 ? "You're owed" : userBalance < 0 ? 'You owe' : 'Settled up'}
                  </p>
                </div>
                <div className={`p-3 rounded-2xl ${userBalance >= 0 ? 'bg-green-100' : 'bg-amber-100'} shrink-0`}>
                  <div className={getBalanceColor(userBalance)}>
                    {getBalanceIcon(userBalance)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card data-testid="stat-members">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] sm:text-xs uppercase tracking-wide text-gray-500 mb-1">Members</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-lg sm:text-xl font-bold text-gray-900 tabular-nums">{group.members.length}</p>
                  <Users className="w-4 h-4 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-group-total">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] sm:text-xs uppercase tracking-wide text-gray-500 mb-1">Group Total</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-lg sm:text-xl font-bold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatCurrency(group.total_spent || group.totalSpent || 0)}
                  </p>
                  <DollarSign className="w-4 h-4 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-0">
              <TabsList className="grid w-full grid-cols-3 bg-gray-100/70 p-1 rounded-xl h-11">
                <TabsTrigger
                  value="expenses"
                  className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900"
                >
                  Expenses
                </TabsTrigger>
                <TabsTrigger
                  value="balances"
                  className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900"
                >
                  Balances
                </TabsTrigger>
                <TabsTrigger
                  value="members"
                  className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900"
                >
                  Members
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              <TabsContent value="expenses" className="space-y-4">
                <h3 className="text-lg font-semibold">Recent Expenses</h3>

                <div className="space-y-3">
                  {expenses.length === 0 ? (
                    <div className="text-center py-8">
                      <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No expenses yet</h4>
                      <p className="text-gray-500 mb-4">Start by adding your first expense to this group</p>
                      <Button onClick={handleAddExpenseClick} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Expense
                      </Button>
                    </div>
                  ) : (
                    expenses.map((expense) => {
                      const category = getCategoryInfo(expense.category);
                      const amount = expense.amount_total || expense.amount || 0;
                      const paidByName = expense.paid_by_name || expense.paidBy?.name || 'Unknown';
                      const splitBetween = expense.splitBetween || [];
                      const perPerson = splitBetween.length > 0 ? (amount / splitBetween.length) : 0;
                      const createdAt = expense.created_at || expense.date;
                      
                      return (
                        <div key={expense.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: `${category.color}20` }}
                              >
                                <div className="w-5 h-5" style={{ color: category.color }}>
                                  <DollarSign className="w-5 h-5" />
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold text-gray-900">{expense.description}</h4>
                                <p className="text-sm text-gray-500">
                                  Paid by {paidByName} • {category.name}
                                </p>
                                <p className="text-xs text-gray-400 flex items-center mt-1">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {timeAgo(createdAt)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3">
                              <div className="text-right">
                                <p className="text-lg font-bold text-gray-900">
                                  {formatCurrency(amount)}
                                </p>
                                {splitBetween.length > 0 && (
                                  <p className="text-sm text-gray-500">
                                    {formatCurrency(perPerson)} each
                                  </p>
                                )}
                              </div>
                              
                              {/* Delete button - only show for owner or expense creator */}
                              {(group?.owner_id === user?.id || expense.paid_by_user_id === user?.id) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteExpenseId(expense.id);
                                    setShowDeleteDialog(true);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </TabsContent>

              <TabsContent value="balances" className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold">Member Balances</h3>
                
                <div className="space-y-3">
                  {memberBalances.map((member) => {
                    const balance = member.balance ?? 0;
                    const absBalance = Math.abs(balance);
                    const isCurrentUser = member.user_id === user?.id;
                    const iOweThisMember = !isCurrentUser && balance > 0;
                    const theyOweMe = !isCurrentUser && balance < 0;
                    const settled = !isCurrentUser && balance === 0;
                    
                    return (
                      <div
                        key={member.user_id || member.id}
                        className="p-4 border border-gray-200 rounded-xl"
                        data-testid={`balance-member-${member.user_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar className="w-10 h-10 shrink-0">
                            <AvatarImage src={member.avatar} alt={member.name} />
                            <AvatarFallback>{member.name?.charAt(0) || '?'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 truncate">
                                  {isCurrentUser ? 'You' : (member.name || 'Unknown')}
                                </h4>
                                <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                                  Paid {formatCurrency(member.paid)} · Owes {formatCurrency(member.owes)}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className={`text-base sm:text-lg font-bold tabular-nums whitespace-nowrap ${getBalanceColor(balance)}`}>
                                  {balance >= 0 ? '+' : '-'}{formatCurrency(absBalance)}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  {balance > 0 ? 'gets back' : balance < 0 ? 'owes' : 'settled up'}
                                </p>
                              </div>
                            </div>

                            {iOweThisMember && (
                              <div className="mt-3 space-y-1.5">
                                <Button
                                  size="default"
                                  data-testid={`pay-btn-${member.user_id}`}
                                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 font-semibold"
                                  onClick={() => setPaymentModal({
                                    open: true,
                                    toUserId: member.user_id,
                                    toUserName: member.name,
                                    amountCents: Math.round(absBalance * 100)
                                  })}
                                >
                                  Pay {formatCurrency(absBalance)}
                                </Button>
                                <button
                                  type="button"
                                  data-testid={`mark-external-btn-${member.user_id}`}
                                  className="w-full text-center text-xs text-gray-500 hover:text-gray-700 transition py-1"
                                  onClick={() => openSettleModal(member.user_id, member.name, balance)}
                                >
                                  Mark as paid externally
                                </button>
                              </div>
                            )}

                            {theyOweMe && (
                              <div className="mt-3 space-y-1.5">
                                <Button
                                  size="default"
                                  variant="outline"
                                  data-testid={`request-btn-${member.user_id}`}
                                  className="w-full h-10 font-medium border-blue-300 text-blue-700 hover:bg-blue-50"
                                  onClick={() => setQrModal({
                                    open: true,
                                    fromUserId: member.user_id,
                                    fromUserName: member.name,
                                    amountCents: Math.round(absBalance * 100)
                                  })}
                                >
                                  <QrCode className="w-4 h-4 mr-2" />
                                  Request {formatCurrency(absBalance)}
                                </Button>
                                <button
                                  type="button"
                                  data-testid={`mark-external-btn-${member.user_id}`}
                                  className="w-full text-center text-xs text-gray-500 hover:text-gray-700 transition py-1"
                                  onClick={() => openSettleModal(member.user_id, member.name, balance)}
                                >
                                  Mark as paid externally
                                </button>
                              </div>
                            )}

                            {settled && (
                              <p className="text-xs text-gray-400 mt-2">All settled up.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Recent Settlements Ledger */}
                <Separator className="my-4" />
                <h3 className="text-base sm:text-lg font-semibold" data-testid="recent-settlements-heading">Recent Payments</h3>
                <div className="space-y-3">
                  {settlements.length === 0 ? (
                    <div className="text-center py-8" data-testid="no-settlements-message">
                      <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">No payments yet</p>
                      <p className="text-gray-400 text-xs mt-1">Pay with card or mark a payment as paid externally to see it here.</p>
                    </div>
                  ) : (
                    settlements.map((s) => {
                      const isFromMe = s.from_user_id === user?.id;
                      const isToMe = s.to_user_id === user?.id;
                      const fromName = isFromMe ? 'You' : (s.from_user_name || 'Someone');
                      const toName = isToMe ? 'You' : (s.to_user_name || 'Someone');
                      const dateStr = formatSettlementDate(s.created_at);

                      return (
                        <div key={s.id} className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-green-50/60" data-testid={`settlement-${s.id}`}>
                          <div className="flex items-center space-x-3">
                            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                              <DollarSign className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {fromName} paid {toName}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {dateStr} · <span className={`font-medium ${s.method === 'card' ? 'text-blue-600' : 'text-green-600'}`}>{s.method === 'card' ? 'Card' : 'Manual'}</span>
                              </p>
                              {s.notes && <p className="text-xs text-gray-400 mt-0.5">{s.notes}</p>}
                            </div>
                          </div>
                          <p className="text-base font-bold text-green-700 shrink-0">
                            {formatCurrency(s.amount_cents / 100)}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </TabsContent>

              <TabsContent value="members" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Group Members</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowInviteForm(!showInviteForm)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Member
                  </Button>
                </div>

                {/* Invite Form */}
                {showInviteForm && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                      <form onSubmit={handleInviteMember} className="space-y-4">
                        <h4 className="font-medium text-blue-900">Invite New Member</h4>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="inviteName" className="text-blue-800">Name</Label>
                            <Input
                              id="inviteName"
                              placeholder="Enter member's name"
                              value={inviteData.name}
                              onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                              className="bg-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="inviteEmail" className="text-blue-800">Email</Label>
                            <Input
                              id="inviteEmail"
                              type="email"
                              placeholder="Enter email address"
                              value={inviteData.email}
                              onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                              className="bg-white"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            type="submit" 
                            className="bg-blue-600 hover:bg-blue-700"
                            disabled={isInviting || !inviteData.name.trim() || !inviteData.email.trim()}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {isInviting ? 'Sending...' : 'Send Invitation'}
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => setShowInviteForm(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-3">
                  {group.members.map((member) => (
                    <div key={member.user_id || member.email} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex flex-wrap items-start gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Avatar className="w-10 h-10 shrink-0">
                            <AvatarImage src={member.avatar} alt={member.name} />
                            <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                              <h4 className="font-semibold text-gray-900 truncate">{member.name}</h4>
                              {member.role === 'owner' && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Owner</Badge>
                              )}
                              {member.status === 'pending' && (
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
                                  Pending
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 min-w-0">
                              <Mail className="w-3 h-3 shrink-0" />
                              <span className="truncate">{member.email}</span>
                            </div>
                          </div>
                        </div>

                        {member.status === 'pending' && (
                          <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendInvite(member.email, member.name)}
                              className="text-xs flex-1 sm:flex-none"
                              data-testid={`resend-invite-${member.email}`}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Resend
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyInviteLink(member)}
                              className="text-xs flex-1 sm:flex-none"
                              data-testid={`copy-invite-${member.email}`}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy link
                            </Button>
                          </div>
                        )}
                        {member.status === 'active' && <ChevronRight className="w-5 h-5 text-gray-400 ml-auto self-center" />}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Delete Group - Minimal inline layout (owner only) */}
        {group?.owner_id === user?.id && (
          <div className="flex items-center justify-between py-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Delete this group and all its expenses permanently
            </p>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete group
            </Button>
          </div>
        )}
      </div>


      {/* Delete Expense Confirmation Dialog */}
      {showDeleteDialog && deleteExpenseId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center text-red-600">
                <Trash2 className="w-5 h-5 mr-2" />
                Delete this expense?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                This will remove the expense and recalculate all balances. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeleteExpenseId(null);
                  }}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteExpense}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete expense
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Delete Group Confirmation Dialog */}
      {showDeleteDialog && !deleteExpenseId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center text-red-600">
                <Trash2 className="w-5 h-5 mr-2" />
                Delete this group?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                This will remove the group and all its expenses for all members. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteGroup}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete group
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mark as paid externally Modal */}
      {showSettleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="settle-up-modal">
          <Card className="max-w-md w-full">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold text-gray-900">
                Mark as paid externally
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Record a cash, Venmo, or Zelle payment to {settleData.toUserName || 'this member'}.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs uppercase tracking-wider text-gray-500">Amount paid</p>
                <div className="flex items-center mt-1">
                  <span className="text-2xl font-bold text-gray-900 mr-1">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    inputMode="decimal"
                    data-testid="settle-amount-input"
                    className="text-2xl font-bold border-0 border-b-2 border-gray-300 rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-500 tabular-nums"
                    value={settleData.amount}
                    onChange={(e) => setSettleData(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSettleModal(false);
                    setSettleData({ toUserId: '', toUserName: '', amount: '' });
                  }}
                  disabled={isSettling}
                  className="flex-1 h-11"
                  data-testid="settle-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSettleUp}
                  disabled={isSettling || !settleData.amount || parseFloat(settleData.amount) <= 0}
                  className="flex-[2] h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  data-testid="settle-confirm-btn"
                >
                  {isSettling ? 'Saving…' : 'Mark as paid'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Card Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.open}
        onClose={() => setPaymentModal({ open: false, toUserId: '', toUserName: '', amountCents: 0 })}
        groupId={groupId}
        toUserId={paymentModal.toUserId}
        toUserName={paymentModal.toUserName}
        amountCents={paymentModal.amountCents}
        onSuccess={refetchGroup}
      />

      {/* QR Collect Modal */}
      <QRCollectModal
        isOpen={qrModal.open}
        onClose={() => setQrModal({ open: false, fromUserId: '', fromUserName: '', amountCents: 0 })}
        groupId={groupId}
        payerUserId={qrModal.fromUserId}
        payerName={qrModal.fromUserName}
        amountCents={qrModal.amountCents}
        onPaymentReceived={refetchGroup}
      />
    </div>
  );
};

export default GroupDetail;